import { useEmailValidation } from "@circle-react/components/OnboardingPage/CreateProfilePage/useEmailValidation";
import { usePaywallCheckoutContext } from "@circle-react/contexts/Paywalls/paywallCheckoutContext";
import { useFormContext } from "react-hook-form";

import { getStepByPaywallId } from "../../../CheckoutWithSession/helpers/SessionStepQueryHelper";
import { CheckoutError } from "../../CheckoutError";
import { useCheckoutSession } from "../../contexts/CheckoutSessionContext";
import { getReferral } from "../../helpers";
import type { CheckoutFormValuesProps } from "../../types/CheckoutFormValuesProps";
import { useHandleCheckoutError } from "../useHandleCheckoutError";
import { useCard } from "./useCard";

export const useOnSubmit = () => {
  const {
    setIsProcessingPayment,
    clearAlerts,
    currentUser,
    previewMutation,
    paywall,
  } = usePaywallCheckoutContext();
  const { session } = useCheckoutSession();
  const { handleCheckoutError } = useHandleCheckoutError();
  const cardHooks = useCard();

  const { validateEmail } = useEmailValidation({ shouldUseDebounce: false });
  const { setError, clearErrors, setFocus } = useFormContext();

  const performMailgunValidation = async (email: string) => {
    if (!currentUser && email) {
      clearErrors("email");
      const validationResult = await validateEmail(email);

      if (validationResult !== true) {
        setError("email", {
          type: "manual",
        });
        setFocus("email");
        return false;
      }
    }
    return true;
  };

  const onSubmit = async (formData: CheckoutFormValuesProps) => {
    formData.coupon_code =
      previewMutation?.variables?.queryParams?.coupon_code || "";

    formData.checkout_session_step_id = getStepByPaywallId(
      paywall.id,
      session,
    )?.id;

    clearAlerts();

    if (getReferral()) {
      formData.referral_metadata = getReferral();
    }

    try {
      setIsProcessingPayment(true);

      const isEmailValid = await performMailgunValidation(formData.email);
      if (!isEmailValid) {
        return;
      }

      await cardHooks.onSubmitMutation.mutateAsync(formData);
    } catch (error: unknown) {
      if (error instanceof CheckoutError) {
        handleCheckoutError(error);
      } else {
        handleCheckoutError(
          CheckoutError.fromUnknownError("useOnSubmit", error),
        );
      }
    } finally {
      setIsProcessingPayment(false);
    }
  };

  return {
    onSubmit,
  };
};
